import { PortalModule } from "@angular/cdk/portal";
import { CommonModule as NgCommonModule } from "@angular/common";
import { ComponentFactoryResolver, ModuleWithProviders, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatDatepickerContent, MatDatepickerModule } from "@angular/material/datepicker";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatLegacyButtonModule as MatButtonModule } from "@angular/material/legacy-button";
import { MatLegacyCardModule as MatCardModule } from "@angular/material/legacy-card";
import { MatLegacyCheckboxModule as MatCheckboxModule } from "@angular/material/legacy-checkbox";
import {
	MatLegacyNativeDateModule as MatNativeDateModule,
	MatLegacyOptionModule as MatOptionModule,
} from "@angular/material/legacy-core";
import { MatLegacyDialogModule as MatDialogModule } from "@angular/material/legacy-dialog";
import { MatLegacyFormFieldModule as MatFormFieldModule } from "@angular/material/legacy-form-field";
import { MatLegacyInputModule as MatInputModule } from "@angular/material/legacy-input";
import { MatLegacyListModule as MatListModule } from "@angular/material/legacy-list";
import { MatLegacyMenuModule as MatMenuModule } from "@angular/material/legacy-menu";
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from "@angular/material/legacy-progress-spinner";
import { MatLegacyRadioModule as MatRadioModule } from "@angular/material/legacy-radio";
import { MatLegacySelectModule as MatSelectModule } from "@angular/material/legacy-select";
import { MatLegacySlideToggleModule as MatSlideToggleModule } from "@angular/material/legacy-slide-toggle";
import { MatLegacySnackBarModule as MatSnackBarModule } from "@angular/material/legacy-snack-bar";
import { MatLegacyTabsModule as MatTabsModule } from "@angular/material/legacy-tabs";
import { MatLegacyTooltipModule as MatTooltipModule } from "@angular/material/legacy-tooltip";
import { MatToolbarModule } from "@angular/material/toolbar";
import { TranslateModule } from "@ngx-translate/core";
import { MatGlobalModule } from "@obg/mat.ui.global";
import { CoreModule, SnackBarCacheService, UiInteractionService } from "@obg/ngx.core";
import { CoreContentModule } from "@obg/ngx.core.content";
import { DevicePostfixPipe, GlobalModule } from "@obg/ngx.global";
import { RouterModule } from "@obg/ngx.router";
import {
	BetaBadgeModule,
	CompactFooterModule,
	ComponentFactoryService,
	CoreUiModule,
	ImageContentDirectiveModule,
	RegulatoryItemsModule,
} from "@obg/ngx.ui.core";
import { GlobalUiModule } from "@obg/ngx.ui.global";
import { isFunction } from "lodash-es";

import { AlertComponent } from "./alert/alert.component";
import { TranslatableAlertService } from "./alert/translatable-alert.service";
import { _MAT_CORE_CONFIG, MAT_CORE_CONFIG } from "./const";
import { BaseMatDialogContainer } from "./dialog/container/base-mat-dialog.container";
import { NativeMatDialogContainer } from "./dialog/container/native-mat-dialog.container";
import { SlideUpMatDialogContainer } from "./dialog/container/slide-up-mat-dialog.container";
import { DialogLazyComponent } from "./dialog/dialog-lazy/dialog-lazy.component";
import { DialogLikeContainer } from "./dialog/dialog-like/dialog-like.container";
import { DialogRouteContainer } from "./dialog/dialog-route/dialog-route.container";
import { DialogLayoutModule } from "./dialog-layout/dialog-layout.module";
import { MessageAlertService } from "./message/message-alert.service";
import { MessageSnackBarService } from "./message/message-snackbar.service";
import { MatCoreConfig } from "./model";
import { SnackBarComponent } from "./snackbar/snackbar.component";
import { TranslatableSnackBarService } from "./snackbar/translatable-snackbar.service";
import { CORE_UI_INTERACTION_TRACKING_MAP } from "./tracking/tracking.const";

export const MAT_UI_CORE_DIRECTIVES: any[] = [
	DialogLikeContainer,
	DialogLazyComponent,
	DialogRouteContainer,
	AlertComponent,
	SnackBarComponent,
	BaseMatDialogContainer,
	SlideUpMatDialogContainer,
	NativeMatDialogContainer,
];

export const MAT_MODULES: any[] = [
	MatButtonModule,
	MatCheckboxModule,
	MatDialogModule,
	MatDividerModule,
	MatFormFieldModule,
	MatIconModule,
	MatMenuModule,
	MatNativeDateModule,
	MatToolbarModule,
	MatInputModule,
	MatSlideToggleModule,
	MatCardModule,
	MatListModule,
	MatSnackBarModule,
	MatProgressSpinnerModule,
	MatBadgeModule,
	MatOptionModule,
	MatSelectModule,
	MatButtonToggleModule,
	MatExpansionModule,
	MatTabsModule,
	MatTooltipModule,
	MatRadioModule,
	PortalModule,
];

const MAT_ENTRY_COMPONENTS = [
	DialogRouteContainer,
	DialogLikeContainer,
	DialogLazyComponent,
	DialogRouteContainer,
	AlertComponent,
	SnackBarComponent,
	BaseMatDialogContainer,
	SlideUpMatDialogContainer,
	MatDatepickerContent,
];

@NgModule({
	imports: [
		NgCommonModule,
		FormsModule,
		CoreModule,
		CoreUiModule,
		CoreContentModule,
		GlobalModule,
		GlobalUiModule,
		MatGlobalModule,
		RouterModule,
		MAT_MODULES,
		ReactiveFormsModule,
		BetaBadgeModule,
		DialogLayoutModule,
		CompactFooterModule,
		MatDatepickerModule,
		RegulatoryItemsModule,
		TranslateModule,
		ImageContentDirectiveModule,
	],
	providers: [
		TranslatableAlertService,
		TranslatableSnackBarService,
		MessageAlertService,
		MessageSnackBarService,
		SnackBarCacheService,
		DevicePostfixPipe,
	],
	exports: [MAT_UI_CORE_DIRECTIVES],
	declarations: [MAT_UI_CORE_DIRECTIVES],
})
export class MatCoreModule {
	constructor(
		uiInteractionService: UiInteractionService,
		componentFactoryService: ComponentFactoryService,
		resolver: ComponentFactoryResolver
	) {
		uiInteractionService.registerTrackingData(CORE_UI_INTERACTION_TRACKING_MAP);
		componentFactoryService.registerLazyLoadedResolver(resolver, ...MAT_ENTRY_COMPONENTS);
	}

	/**
	 * Call `GlobalUiLazyImageModule.forRoot` in your application module.
	 *
	 * @param config Config is required, specify it in `forRoot` or alternatively using `GLOBAL_LAZY_IMAGE_CONFIG` token to utilize DI.
	 */
	static forRoot(config?: MatCoreConfig | (() => MatCoreConfig)): ModuleWithProviders<MatCoreModule> {
		return {
			ngModule: MatCoreModule,
			providers: [
				{ provide: MAT_CORE_CONFIG, useFactory: _coreConfigFactory, deps: [_MAT_CORE_CONFIG] },
				{ provide: _MAT_CORE_CONFIG, useValue: config },
			],
		};
	}
}

/**
 * @internal
 */
export function _coreConfigFactory(config?: MatCoreConfig | (() => MatCoreConfig)) {
	return isFunction(config) ? config() : config;
}
